import { getShareClassName, parseHTMLFragment } from "@/lib";
import type { ShareClass } from "@/types";
import {
	getAnnualisedData,
	getAnnualisedHeaders,
	getLabelReplacedData,
} from "@components/PerformanceTabs/helpers";
import {
	type ChartConfig,
	type Entities,
	type MonthlyData,
	PerformanceSectionSchema,
} from "@components/PerformanceTabs/types";
import { useMemo } from "react";

export default function usePerformanceSectionData(
	data?: string,
	shareClass?: ShareClass | null,
) {
	const parsedData = useMemo(() => parseHTMLFragment(data as string), [data]);
	const validatedData = useMemo(
		() => PerformanceSectionSchema.safeParse(parsedData),
		[parsedData],
	);
	const labelsMap = useMemo(() => {
		return shareClass
			? {
					Fund: getShareClassName(shareClass),
				}
			: ({} as Record<string, string>);
	}, [shareClass]);

	if (!data) {
		return null;
	}
	if (!validatedData.success) {
		console.log(parsedData);
		console.error(validatedData.error);
		return null;
	}
	const removeBlankRows = (_data: string[][]) => {
		const truncatedData = _data.filter(([_label, ...rowData]) => {
			const bool = rowData.some((cell) => !!cell && cell.trim() !== "-");
			return bool;
		});
		const availableLabels = truncatedData.flatMap((row) => row[0]);
		return [truncatedData, availableLabels] as [string[][], string[]];
	};

	const { summary, yearOnYear, monthly } = validatedData.data;
	const [cumulative, annualised] = summary;

	const [truncatedCumulativeData, truncatedLabels] = removeBlankRows(
		cumulative.data,
	);
	const cumulativeData = getLabelReplacedData(
		truncatedCumulativeData as string[][],
		labelsMap,
	);
	const cumulativeHeaders = cumulative.headers;
	const annualisedData = getAnnualisedData(annualised.data, truncatedLabels);
	const annualisedHeaders = getAnnualisedHeaders(annualised.headers);
	const [truncatedYoYData] = removeBlankRows(yearOnYear.data);
	const yearOnYearData = getLabelReplacedData(truncatedYoYData, labelsMap);
	const partitionedMonthlyData = monthly ? partitionData(monthly) : null;
	const labelReplacedMonthlyData = partitionedMonthlyData
		? getLabelReplacedMonthlyData(partitionedMonthlyData, labelsMap)
		: null;
	const note = validatedData.data.note ?? { text: "" };
	const ret = {
		summary: {
			cumulative: {
				data: cumulativeData,
				headers: cumulativeHeaders,
				header: cumulative.header,
			},
			annualised: {
				data: annualisedData,
				headers: annualisedHeaders,
				header: annualised.header,
			},
		},
		yearOnYear: {
			data: yearOnYearData,
			headers: yearOnYear.headers,
		},
		monthly:
			partitionedMonthlyData && labelReplacedMonthlyData
				? {
						data: labelReplacedMonthlyData.data,
						headers: partitionedMonthlyData.months,
						years: partitionedMonthlyData.years,
					}
				: undefined,
		note: "text" in note ? `${note.text}` : "",
		entities: ([] as Entities[]).concat(validatedData.data.entities),
		messages: {
			lessThanYearOld: validatedData.data.messages.lessThanYearOld.text,
		},
		chart: validatedData.data.chart,
	};
	return ret;
}

type TabData = {
	data: string[][];
	headers: string[];
	header?: string;
};
type SummaryData = {
	cumulative: TabData;
	annualised: TabData;
};
type EntitiesData = {
	data: [string, string | null][];
};
type MonthlyTabData = {
	data: PartitionedMonthlyData["data"];
	headers: PartitionedMonthlyData["months"];
	years: PartitionedMonthlyData["years"];
};
export type PerformanceTabData = {
	summary: SummaryData;
	yearOnYear: TabData;
	monthly?: MonthlyTabData;
	note: string;
	entities: EntitiesData[];
	messages: {
		lessThanYearOld: string;
	};
	chart?: ChartConfig;
};

export interface PartitionedMonthlyData {
	years: string[];
	months: string[]; // always ["Jan", "Feb", ..., "Dec"]
	data: {
		[year: string]: {
			data: string[][];
		};
	};
}
function partitionData(input: MonthlyData): PartitionedMonthlyData {
	// Fixed list of month abbreviations in the desired order.
	const monthNames = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	];

	// Map month numbers from headers (e.g., "01", "02", etc.) to month abbreviations.
	const monthMap: { [key: string]: string } = {
		"01": "Jan",
		"02": "Feb",
		"03": "Mar",
		"04": "Apr",
		"05": "May",
		"06": "Jun",
		"07": "Jul",
		"08": "Aug",
		"09": "Sep",
		"10": "Oct",
		"11": "Nov",
		"12": "Dec",
	};

	// Build a mapping from year to a mapping of month abbreviation to header index.
	// Each header index corresponds to the position in input.headers and in each row of input.data.
	const yearMonthMapping: Record<string, Record<string, number>> = {};

	// Iterate over headers (skip index 0 which holds row labels)
	for (let i = 1; i < input.headers.length; i++) {
		const header = input.headers[i].trim();
		// Assume header format is "YYYY-MM-DD"
		const parts = header.split("-");
		if (parts.length < 2) continue;
		const year = parts[0];
		const monthNumber = parts[1];
		const monthAbbrev = monthMap[monthNumber] || monthNumber;
		if (!yearMonthMapping[year]) {
			yearMonthMapping[year] = {};
		}
		// Record the header index for this month.
		yearMonthMapping[year][monthAbbrev] = i;
	}

	// Get a sorted array of years.
	const years = Object.keys(yearMonthMapping).sort((y1, y2) =>
		y2.localeCompare(y1),
	);

	const result: PartitionedMonthlyData = {
		years: [],
		months: monthNames,
		data: {},
	};

	// For each year, build the partitioned rows.
	for (const year of years) {
		const mapping = yearMonthMapping[year]; // e.g., { Jan: index, Feb: index, ... }
		// Build a new row for each original row in input.data.
		const partitionedRows = input.data.map((row) => {
			// Start the new row with the label (the first element).
			const newRow: string[] = [row[0]];
			// For each month in fixed order, grab the corresponding value if available; otherwise, add '-'.
			for (const month of monthNames) {
				if (mapping[month] !== undefined) {
					newRow.push(row[mapping[month]]);
				} else {
					newRow.push("-");
				}
			}
			return newRow;
		});

		// Filtering Step 1: Check that the "Fund" row exists and has at least one non-dash value.
		const fundRow = partitionedRows.find(
			(row) => row[0].toLowerCase() === "fund",
		);
		if (!fundRow || fundRow.slice(1).every((cell) => cell === "-")) {
			continue; // Skip this year if the Fund row is absent or has no data.
		}

		// Filtering Step 2: Remove any rows that are entirely empty (all dashes after the label).
		// const nonEmptyRows = partitionedRows.filter((row) =>
		// 	row.slice(1).some((cell) => cell !== "-"),
		// );

		// Only add this year if there is at least one row (Fund row is guaranteed by previous check).
		if (partitionedRows.length > 0) {
			result.data[year] = { data: partitionedRows };
			result.years.push(year);
		}
	}

	return result;
}

function getLabelReplacedMonthlyData(
	partitionedData: PartitionedMonthlyData,
	labelsMap: Record<string, string>,
) {
	const partitionedDataCopy = { ...partitionedData };
	for (const year of partitionedDataCopy.years) {
		const data = partitionedDataCopy.data[year].data;
		for (let i = 0; i < data.length; i++) {
			const row = data[i];
			for (let j = 0; j < row.length; j++) {
				const cell = row[j];
				if (cell in labelsMap) {
					row[j] = labelsMap[cell];
				}
			}
		}
	}
	return partitionedDataCopy;
}
